/**
 * Sentry PHI/PII scrubber — defence in depth before events leave the
 * server. Sentry's default `sendDefaultPii: false` covers some
 * categories, but does not cover:
 *   - email addresses embedded in error messages or extras
 *   - AU phone numbers in any field
 *   - Cliniko `patient_id` / `patientId` / `cliniko_id` value fields
 *   - request/response body fragments captured by integrations
 *
 * AHPRA + Australian Privacy Act 1988 (APP 6, APP 11) compliance:
 * patient identifiers and contact details are health information
 * (or directly link to it) and must not leave Australia in plaintext
 * via Sentry's US ingest.
 *
 * Pure-function module — no Sentry SDK dependency, so it can be
 * unit-tested against synthetic event objects without a real DSN.
 */

const EMAIL_RE = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;

// AU phone formats:
//   +61 4XX XXX XXX     mobile, intl
//   +61 X XXXX XXXX     landline, intl
//   04XX XXX XXX        mobile, national
//   (0X) XXXX XXXX      landline, national
//   0X XXXX XXXX        landline, national, no parens
// Spaces are optional throughout.
const PHONE_RE =
  /(?:\+61\s?4\d{2}\s?\d{3}\s?\d{3}|\+61\s?\d\s?\d{4}\s?\d{4}|\b04\d{2}\s?\d{3}\s?\d{3}\b|\(0\d\)\s?\d{4}\s?\d{4}|\b0\d\s?\d{4}\s?\d{4}\b)/g;

// Field/key names that hold a Cliniko patient identifier — value is
// always redacted, regardless of the surface (request body, extras,
// breadcrumb data, frame vars).
const PATIENT_ID_KEYS = new Set([
  'patient_id',
  'patientid',
  'cliniko_id',
  'clinikoid',
  'cliniko_patient_id',
  'clinikopatientid',
]);

// Sensitive header names — Sentry strips Authorization/Cookie by
// default but we explicitly belt-and-brace it here in case a
// downstream integration re-adds them.
const SENSITIVE_HEADER_KEYS = new Set([
  'authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
  'x-auth-token',
  'proxy-authorization',
]);

const PII_KEY_HINTS = new Set([
  'email',
  'phone',
  'mobile',
  'password',
  'token',
  'secret',
  'apikey',
  'api_key',
]);

const REDACT_STRING = (s: string): string =>
  s.replace(EMAIL_RE, '[email]').replace(PHONE_RE, '[phone]');

/**
 * Recursively walk a value. Strings are pattern-scrubbed. Object keys
 * matching the PHI/PII allowlist have their values fully redacted.
 *
 * Cycles are tracked via a WeakSet so self-referential event objects
 * cannot stack-overflow the scrubber.
 */
function scrubValue(value: unknown, seen: WeakSet<object> = new WeakSet()): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') return REDACT_STRING(value);
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return value;
  }
  if (Array.isArray(value)) {
    if (seen.has(value)) return '[circular]';
    seen.add(value);
    return value.map((v) => scrubValue(v, seen));
  }
  if (typeof value === 'object') {
    if (seen.has(value as object)) return '[circular]';
    seen.add(value as object);
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const lk = k.toLowerCase();
      if (PATIENT_ID_KEYS.has(lk)) {
        out[k] = '[patient_id]';
        continue;
      }
      if (SENSITIVE_HEADER_KEYS.has(lk)) {
        out[k] = '[redacted]';
        continue;
      }
      if (PII_KEY_HINTS.has(lk) && typeof v === 'string') {
        // Force-redact even if the value doesn't match a regex (e.g.
        // already-tokenised phone like "0400000000" without spacing
        // could miss the boundary in some edge inputs).
        out[k] = lk === 'email' ? '[email]' : '[redacted]';
        continue;
      }
      out[k] = scrubValue(v, seen);
    }
    return out;
  }
  return value;
}

/**
 * Minimal subset of the Sentry event shape we touch. Kept structural
 * to avoid coupling tests to `@sentry/types` internals.
 */
export interface ScrubbableEvent {
  message?: unknown;
  extra?: unknown;
  contexts?: unknown;
  tags?: unknown;
  user?: unknown;
  request?: { headers?: unknown; cookies?: unknown; data?: unknown; query_string?: unknown };
  breadcrumbs?: Array<{ message?: unknown; data?: unknown; [k: string]: unknown }>;
  exception?: {
    values?: Array<{
      value?: unknown;
      stacktrace?: { frames?: Array<{ vars?: unknown; [k: string]: unknown }> };
      [k: string]: unknown;
    }>;
  };
  [k: string]: unknown;
}

/**
 * Scrub a Sentry event in place-equivalent fashion (returns a new
 * object; does not mutate the input). Safe to call from
 * `Sentry.init({ beforeSend })`.
 */
export function scrubSentryEvent<T extends ScrubbableEvent>(event: T): T {
  if (!event || typeof event !== 'object') return event;
  const out: ScrubbableEvent = { ...event };

  if (typeof out.message === 'string') {
    out.message = REDACT_STRING(out.message);
  }

  if (out.extra) out.extra = scrubValue(out.extra) as T['extra'];
  if (out.contexts) out.contexts = scrubValue(out.contexts) as T['contexts'];
  if (out.tags) out.tags = scrubValue(out.tags) as T['tags'];
  if (out.user) out.user = scrubValue(out.user) as T['user'];

  if (out.request) {
    out.request = {
      ...out.request,
      headers: out.request.headers ? scrubValue(out.request.headers) : out.request.headers,
      cookies: out.request.cookies ? '[redacted]' : out.request.cookies,
      data: out.request.data ? scrubValue(out.request.data) : out.request.data,
      query_string:
        typeof out.request.query_string === 'string'
          ? REDACT_STRING(out.request.query_string)
          : out.request.query_string,
    };
  }

  if (Array.isArray(out.breadcrumbs)) {
    out.breadcrumbs = out.breadcrumbs.map((bc) => ({
      ...bc,
      message: typeof bc.message === 'string' ? REDACT_STRING(bc.message) : bc.message,
      data: bc.data ? scrubValue(bc.data) : bc.data,
    }));
  }

  if (out.exception?.values && Array.isArray(out.exception.values)) {
    out.exception = {
      ...out.exception,
      values: out.exception.values.map((ex) => ({
        ...ex,
        value: typeof ex.value === 'string' ? REDACT_STRING(ex.value) : ex.value,
        stacktrace: ex.stacktrace
          ? {
              ...ex.stacktrace,
              frames: ex.stacktrace.frames?.map((f) => ({
                ...f,
                vars: f.vars ? (scrubValue(f.vars) as Record<string, unknown>) : f.vars,
              })),
            }
          : ex.stacktrace,
      })),
    };
  }

  return out as T;
}

// Internal helpers exported for unit testing only.
export const __test = { REDACT_STRING, scrubValue, EMAIL_RE, PHONE_RE };
